import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import type { Database } from "@/integrations/supabase/types";
import {
  ArrowLeft,
  PlayCircle,
  CheckCircle,
  Check,
  Clock,
  FileText,
  HelpCircle,
  Calendar,
} from "lucide-react";
import { StageTaskChecklist } from "@/components/journey/StageTaskChecklist";
import { JourneyDocumentUpload } from "@/components/journey/JourneyDocumentUpload";
import { sanitizeVideoEmbed } from "@/lib/sanitize";

type StageType = Database["public"]["Enums"]["fulfillment_stage_type"];

type StageData = {
  id: string;
  stage_type: StageType;
  order_number: number;
  title: string;
  description: string | null;
  video_url: string | null;
  video_embed_code: string | null;
  estimated_duration_days: number | null;
  key_actions: any;
  resources: any;
};

type UserProgress = {
  agreement_signed: boolean;
  agreement_signed_date: string | null;
  typeform_submitted: boolean;
  typeform_submitted_date: string | null;
  onboarding_data: any;
};

export default function StageDetail() {
  const { stageType } = useParams<{ stageType: string }>();
  const [stage, setStage] = useState<StageData | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [userProfile, setUserProfile] = useState<{ full_name?: string; display_name?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStageData();
  }, [stageType, user]);

  const fetchStageData = async () => {
    if (!user || !stageType) return;

    try {
      // Fetch user profile for first name
      const { data: profileData } = await supabase
        .from("profiles")
        .select("full_name, display_name")
        .eq("id", user.id)
        .single();
      
      setUserProfile(profileData || null);

      // Fetch stage details
      const { data: stageData, error: stageError } = await supabase
        .from("fulfillment_stages")
        .select("*")
        .eq("stage_type", stageType.toUpperCase() as StageType)
        .single();

      if (stageError) throw stageError;
      setStage(stageData);

      // Fetch user progress for agreement/typeform status
      const { data: progressData, error: progressError } = await supabase
        .from("user_fulfillment_progress")
        .select("agreement_signed, agreement_signed_date, typeform_submitted, typeform_submitted_date, onboarding_data")
        .eq("user_id", user.id)
        .single();

      if (progressError && progressError.code !== "PGRST116") {
        throw progressError;
      }
      setUserProgress(progressData || null);

      // Check if stage is completed
      const { data: completionData, error: completionError } = await supabase
        .from("stage_completions")
        .select("*")
        .eq("user_id", user.id)
        .eq("stage_type", stageType.toUpperCase() as StageType)
        .single();

      if (completionError && completionError.code !== "PGRST116") {
        throw completionError;
      }

      setIsCompleted(!!completionData);

      // Log stage view (analytics tracked on backend)
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const handleContactSupport = () => {
    navigate("/support");
  };

  const getFirstName = () => {
    if (userProfile?.full_name) {
      return userProfile.full_name.split(' ')[0];
    }
    if (userProfile?.display_name) {
      return userProfile.display_name.split(' ')[0];
    }
    return "there";
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  if (!stage) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-8 px-4 text-center">
          <h1 className="text-2xl font-bold mb-4">Stage Not Found</h1>
          <Button onClick={() => navigate("/journey")}>
            Back to Journey
          </Button>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 2 - Agreement stage
  if (stage.order_number === 2) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Agreement Header */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-4">Your Agreement With Us</h1>
            <p className="text-xl text-muted-foreground">
              Hey {getFirstName()}, this is the last thing we need before we get to work making the UNA.
            </p>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091456110?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Agreement Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          {/* CTA Section */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-8">Ready to review and sign?</h2>
            
            {userProgress?.agreement_signed ? (
              <div className="max-w-md mx-auto bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <p className="text-green-800 font-bold text-lg">Agreement Signed!</p>
                </div>
                {userProgress.agreement_signed_date && (
                  <p className="text-sm text-green-600">
                    Signed: {new Date(userProgress.agreement_signed_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <Button 
                asChild 
                size="lg" 
                className="bg-black hover:bg-black/90 text-white px-8 py-6 text-lg rounded-md"
              >
                <a
                  href="https://wealthysteward.com/agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <span>Sign the Agreement</span>
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </a>
              </Button>
            )}
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 8 - Documents Delivered page
  if (stage.order_number === 8) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">Documents Delivered</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Based on what we see on our side, your documents have been delivered to you already. How exciting!
              </p>
              
              <p>
                Click this video to see exactly what to do next!
              </p>
              
              <p className="font-semibold">
                Here is your step by step guide on what to do next:
              </p>
              
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>Scan your docs and store them safely.</li>
                <li>Take a moment and take in every single word.</li>
                <li>Sign your documents including the banking minutes</li>
                <li>Bank appointment with bank to open your account</li>
                <li>
                  <a 
                    href="https://wealthysteward.com/banking" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Click here
                  </a> for all the detailed instructions to open your bank account smoothly.
                </li>
              </ol>
              
              <p className="mt-6">
                Once your bank account is open, reply to our email with the following hashtag
              </p>
              
              <p className="font-bold text-xl">
                #BankAccountOpen
              </p>
              
              <p>
                We will then send you a booking link to have your strategy session with us on your personalized next steps
              </p>
              
              <p className="font-semibold">
                Go. Fight. Win.
              </p>
              
              <p className="font-semibold">
                Seth
              </p>
            </div>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091456049?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Documents Delivered Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          {/* Document Upload Section */}
          <JourneyDocumentUpload />

          <Separator className="my-8" />

          {/* Stage-specific Tasks */}
          <StageTaskChecklist stageType={stageType?.toUpperCase() || ''} />

          <Separator className="my-8" />

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 9 - Banking Complete page
  if (stage.order_number === 9) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Stage-specific Tasks */}
          <StageTaskChecklist stageType={stageType?.toUpperCase() || ''} />

          <Separator className="my-8" />

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">Banking Setup Complete</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p className="text-2xl font-semibold text-foreground">
                Congratulations! 🎉
              </p>
              
              <p>
                You've successfully completed the banking setup for your UNA. This was the hardest part of the entire onboarding process, and you did it!
              </p>
              
              <p className="font-semibold text-foreground">
                What happens next?
              </p>
              
              <p>
                Now that your bank accounts are open, you're officially onboarded and ready to start your stewardship journey. We'll be reaching out to schedule your strategy session where we'll discuss your personalized next steps.
              </p>

              <div className="bg-card border rounded-lg p-6 my-6">
                <h3 className="font-semibold text-lg mb-3">Important Next Steps:</h3>
                <ol className="list-decimal list-inside space-y-2 ml-2">
                  <li>Make sure to store your banking documents safely</li>
                  <li>Consider bringing in the survivorship document to the bank (a few days after opening)</li>
                  <li>Start building that banking relationship - it's all about relationships with UNAs</li>
                  <li>Keep an eye out for our email with your strategy session booking link</li>
                </ol>
              </div>

              <p className="font-semibold text-foreground">
                Need to review the banking instructions?
              </p>
              
              <p>
                <a 
                  href="https://wealthysteward.com/banking" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Click here to view the full banking guide
                  <ArrowLeft className="w-4 h-4 rotate-180" />
                </a>
              </p>
              
              <p className="font-semibold text-xl mt-8">
                Go. Fight. Win.
              </p>
              
              <p className="font-semibold">
                Seth
              </p>
            </div>
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 10 - Strategy Session page
  if (stage.order_number === 10) {
    const currentMonth = new Date().toISOString().slice(0, 7); // Format: YYYY-MM
    
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4">Complete Your Strategy Session with Seth!</h1>
            <p className="text-xl text-muted-foreground">
              Hey {getFirstName()}, you're almost done! Let's finalize your personalized UNA strategy.
            </p>
          </div>

          {/* Content */}
          <div className="mb-8">
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                We tell people all the time that is the hardest part of the whole deal, because it is. We are downhill from here.
              </p>
              
              <p>
                First, click the Strategy Intake button below. You'll be taken to the strategy intake form. After you submit it, you'll be automatically redirected back to this app.
              </p>
              
              <p>
                This form helps us prepare for your final strategy session with Seth, where we'll discuss your personalized next steps and answer any questions you have about operating your UNA.
              </p>
              
              <div className="bg-card border rounded-lg p-6 my-6">
                <h3 className="font-semibold text-lg mb-3 text-foreground">What to Expect:</h3>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Complete the strategy intake form (15-20 minutes)</li>
                  <li>Review your previous survey responses</li>
                  <li>Schedule your strategy session with Seth</li>
                  <li>Get personalized guidance on your UNA operations</li>
                </ul>
              </div>

              <p>
                Then you'll be ready to have some personalized strategy with Seth on the next steps to put this fully online.
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-4 mb-12">
            <Button 
              asChild 
              size="lg" 
              className="bg-black hover:bg-black/90 text-white px-8 py-6 text-lg rounded-md w-full max-w-md"
            >
              <a
                href={`https://wealthysteward.com/intake-form?v=2&email=${encodeURIComponent(user?.email || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <span>Start the Strategy Intake</span>
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </a>
            </Button>
            
            <Button 
              asChild 
              size="lg" 
              variant="outline"
              className="px-8 py-6 text-lg rounded-md w-full max-w-md text-center"
            >
              <a
                href={`https://wealthysteward.com/UNA-strategy-session#/?month=${currentMonth}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 whitespace-normal"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                <span>Book Your 30 Minute Strategy Session</span>
              </a>
            </Button>
            
            {/* Fallback link */}
            <div className="text-center pt-2">
              <p className="text-sm text-muted-foreground mb-2">
                Already submitted and got stuck?
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/strategy-complete')}
              >
                Click here to continue
              </Button>
            </div>
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 11 - Onboarding Complete page
  if (stage.order_number === 11) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">🎉 Congratulations! Your Onboarding is Complete!</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p className="text-2xl font-semibold text-foreground">
                Welcome to the Wealthy Steward Community! 🎊
              </p>
              
              <p>
                You've successfully completed all the essential steps to get your UNA up and running. This is an incredible milestone in your stewardship journey.
              </p>
              
              <p className="font-semibold text-foreground">
                What You've Accomplished:
              </p>
              
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Reviewed and signed your agreement</li>
                <li>Completed the onboarding questionnaire</li>
                <li>Obtained your EIN</li>
                <li>Received your formation documents</li>
                <li>Opened your bank accounts</li>
                <li>Completed your strategy survey</li>
              </ul>

              <p className="font-semibold text-foreground mt-6">
                What's Next?
              </p>
              
              <p>
                Now begins the exciting part - putting your UNA to work! Continue with the Foundation tasks below to deepen your understanding and start implementing your personalized strategy.
              </p>
            </div>
          </div>

          <Separator className="my-8" />

          {/* Stage-specific Tasks */}
          <StageTaskChecklist stageType={stageType?.toUpperCase() || ''} />

          <Separator className="my-8" />

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 5 - Filings Received page
  if (stage.order_number === 5) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">{getFirstName()}</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                We just got your registration back from California. Now the waiting game is over and we are on it.
              </p>
              
              <p>
                We will draft your documents now and the next time you hear from us will be when your documents are in the mail to you via 2-Day FedEx
              </p>
              
              <p>
                So exciting!
              </p>
              
              <p className="font-semibold">
                Go. Fight. Win.
              </p>
              
              <p className="font-semibold">
                Seth
              </p>
            </div>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091482323?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Filings Received Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          <Separator className="my-8" />

          {/* Stage-specific Tasks */}
          <StageTaskChecklist stageType={stageType?.toUpperCase() || ''} />

          <Separator className="my-8" />

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 7 - Documents Sent page
  if (stage.order_number === 7) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">{getFirstName()}</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p className="font-semibold">
                Your documents are on the way!
              </p>
              
              <p>
                In just a couple of days, you will be holding all of the beautiful documents that you need to take your UNA forward into the world, beginning with opening your bank account.
              </p>
              
              <p className="font-semibold">
                Go. Fight. Win.
              </p>
              
              <p className="font-semibold">
                Seth
              </p>
            </div>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091456074?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Documents Sent Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          <Separator className="my-8" />

          {/* Stage-specific Tasks */}
          <StageTaskChecklist stageType={stageType?.toUpperCase() || ''} />

          <Separator className="my-8" />

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 6 - Strategy Survey page
  if (stage.order_number === 6) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">UNA Strategy Survey (For Members Only)</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                This form is your space to organize and track all the key elements of your personalized UNA strategy.
              </p>
              
              <p>
                You may be completing it well before your strategy call with Seth. That&apos;s perfect!
              </p>
              
              <p>
                The survey is designed as a <strong>living document.</strong> You can return anytime to update or add information as your situation evolves. The most recent answers you provide will always be saved, so as you consider new entities, assets, or goals, you can refine your strategy here.
              </p>
              
              <p>
                Your responses will help us prepare and ensure our time together is as productive as possible.
              </p>
              
              <p>
                Once your UNA setup is nearly complete - including opening bank accounts - the final step will be your strategy session with Seth. You will know when you get to that step 🙂
              </p>
              
              <p>
                As always, you can reach us with questions at stewards@wealthysteward.com (our private members-only email).
              </p>
              
              <p className="font-semibold">
                The Wealthy Steward Team
              </p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-12">
            <Button 
              asChild 
              size="lg" 
              className="bg-black hover:bg-black/90 text-white px-8 py-6 text-lg rounded-md"
            >
              <a
                href={`https://wealthysteward.com/intake-form?email=${encodeURIComponent(user?.email || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <span>Complete Strategy Survey</span>
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </a>
            </Button>
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 4 - Filings Update page
  if (stage.order_number === 4) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">Great News !</h1>
            
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                We&apos;ve submitted your paperwork to the state of California! Now, it is in their hands. They typically take 2-4 weeks to approve these documents.
              </p>
              
              <p>
                After that, we will complete all your documentation, put it in the mail to you via 2-day FedEx and then you will have everything that you need to open your bank accounts.
              </p>
              
              <p>
                We will let you know as soon as we have your paperwork back from them.
              </p>
              
              <p>
                Until then, if you have any questions, please email support@wealthysteward.com
              </p>
              
              <p className="font-semibold">
                Go. Fight. Win.
              </p>
              
              <p className="font-semibold">
                Seth
              </p>
            </div>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091456088?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Filings Update Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for Step 3 - Next Steps page
  if (stage.order_number === 3) {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-4">Congratulations, you are fully onboard !</h1>
            <p className="text-xl text-muted-foreground">
              We're ready to kick off the entity formation process for you. Watch this video to know what happens next...
            </p>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091456093?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Next Steps Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          {/* Content Section */}
          <div className="text-center mb-12 space-y-6">
            <p className="text-lg text-muted-foreground">
              We will keep in touch with you along the way, letting you know when every step is completed.
            </p>
            
            <p className="text-lg text-muted-foreground">
              We are so excited for your Private Stewardship journey to get started and we'll work as fast and efficiently as we can while keeping you updated on the progress.
            </p>

            <p className="text-lg italic text-muted-foreground mt-8">
              Seth, Jason, and the Wealthy Steward Team
            </p>
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Special layout for UNCATEGORIZED stage (Welcome page)
  if (stage.stage_type === "UNCATEGORIZED") {
    return (
      <MainLayout>
        <div className="container max-w-4xl py-12 px-4">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate("/journey")}
            className="mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Journey
          </Button>

          {/* Welcome Header */}
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-4">Welcome Aboard !</h1>
            <p className="text-xl text-muted-foreground">
              Thank you for joining us on the ship called Private StewardSHIP 😎 Here are the next steps...
            </p>
          </div>

          {/* Video Section */}
          <div className="mb-12">
            <div className="aspect-video rounded-lg overflow-hidden shadow-lg bg-black relative" style={{ padding: '56.25% 0 0 0' }}>
              <iframe
                src="https://player.vimeo.com/video/1091459337?h=9b8f8e8e8e&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Welcome Video"
              />
            </div>
            <script src="https://player.vimeo.com/api/player.js"></script>
          </div>

          <Separator className="my-8" />

          {/* Stage-specific Tasks */}
          <StageTaskChecklist stageType={stageType?.toUpperCase() || ''} />

          <Separator className="my-8" />

          {/* CTA Section */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Ready for the next steps?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              After you've watched the video, you can click below to get started.
            </p>
            
            {userProgress?.typeform_submitted ? (
              <div className="max-w-md mx-auto bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <p className="text-green-800 font-bold text-lg">Onboarding Complete!</p>
                </div>
                {userProgress.typeform_submitted_date && (
                  <p className="text-sm text-green-600 mb-3">
                    Submitted: {new Date(userProgress.typeform_submitted_date).toLocaleDateString()}
                  </p>
                )}
                {userProgress.onboarding_data && (
                  <div className="mt-4 text-sm text-gray-700 space-y-2 text-left">
                    {userProgress.onboarding_data.legal_first_name && (
                      <p><strong>Name:</strong> {userProgress.onboarding_data.legal_first_name} {userProgress.onboarding_data.legal_last_name}</p>
                    )}
                    {userProgress.onboarding_data.legal_address && (
                      <p><strong>Address:</strong> {userProgress.onboarding_data.legal_address}, {userProgress.onboarding_data.legal_city}, {userProgress.onboarding_data.legal_state} {userProgress.onboarding_data.legal_zip}</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <Button 
                asChild 
                size="lg" 
                className="bg-black hover:bg-black/90 text-white px-8 py-6 text-lg rounded-md"
              >
                <a
                  href={`https://wealthysteward.com/una-onboarding-questionnaire?step1&isDynamicPage=0&email=${encodeURIComponent(user?.email || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2"
                >
                  <span>Start the Onboarding</span>
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </a>
              </Button>
            )}
          </div>

          {/* Support Button */}
          <div className="flex justify-center pt-8 border-t">
            <Button
              variant="outline"
              onClick={handleContactSupport}
              className="px-8"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Need Help?
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Default layout for other stages
  return (
    <MainLayout>
      <div className="container max-w-4xl py-8 px-4">
        {/* Header */}
        <Button
          variant="ghost"
          onClick={() => navigate("/journey")}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Journey
        </Button>

        {/* Stage Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Badge variant="outline" className="text-[#c9a961] border-[#c9a961]">
              Stage {stage.order_number}
            </Badge>
            {isCompleted && (
              <Badge className="bg-[#1a365d]">
                <CheckCircle className="w-3 h-3 mr-1" />
                Completed
              </Badge>
            )}
          </div>
          <h1 className="text-4xl font-bold mb-3 text-[#1a365d]">
            {stage.title}
          </h1>
          <p className="text-lg text-muted-foreground">{stage.description}</p>
        </div>

        {/* Video Section */}
        {stage.video_embed_code ? (
          <Card className="mb-8 overflow-hidden">
            <div
              className="aspect-video bg-secondary flex items-center justify-center"
              dangerouslySetInnerHTML={{ __html: sanitizeVideoEmbed(stage.video_embed_code) }}
            />
          </Card>
        ) : stage.video_url ? (
          <Card className="mb-8 overflow-hidden">
            <div className="aspect-video bg-secondary flex items-center justify-center">
              <div className="text-center">
                <PlayCircle className="w-16 h-16 text-[#c9a961] mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  Video coming soon
                </p>
                <Button variant="outline" disabled>
                  Watch Introduction
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="mb-8 p-8 text-center bg-[#f7f5f0]">
            <PlayCircle className="w-12 h-12 text-[#c9a961] mx-auto mb-3" />
            <p className="text-muted-foreground">
              Video content will be added soon
            </p>
          </Card>
        )}

        {/* Key Actions */}
        {Array.isArray(stage.key_actions) && stage.key_actions.length > 0 && (
          <Card className="mb-8 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-5 h-5 text-[#c9a961]" />
              <h2 className="text-xl font-bold text-[#1a365d]">
                Key Actions
              </h2>
            </div>
            <ul className="space-y-3">
              {stage.key_actions.map((action: any, index: number) => (
                <li key={index} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#c9a961]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-[#c9a961]">
                      {index + 1}
                    </span>
                  </div>
                  <span className="text-muted-foreground">{action}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Resources */}
        {Array.isArray(stage.resources) && stage.resources.length > 0 && (
          <Card className="mb-8 p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-[#c9a961]" />
              <h2 className="text-xl font-bold text-[#1a365d]">
                Resources & Documents
              </h2>
            </div>
            <ul className="space-y-2">
              {stage.resources.map((resource: any, index: number) => (
                <li
                  key={index}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <div className="w-2 h-2 rounded-full bg-[#c9a961]" />
                  {resource}
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Separator className="my-8" />

        {/* Action Buttons */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={handleContactSupport}
            className="px-8"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Need Help?
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
